import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Avatar,
  Alert,
  Snackbar,
  Tooltip,
  Container
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as OutlineIcon,
  MovieFilter as ScriptIcon,
  CropLandscape as StoryboardIcon,
  PlaylistAddCheck as TasksIcon,
  CalendarToday as CalendarIcon,
  Work as CrewIcon,
  ViewList as OneLinerIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  AssignmentTurnedIn as ResourcesIcon,
  Call as CallSheetIcon,
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon,
  Videocam as VideocamIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Flag as FlagIcon,

  TimelineOutlined as TimelineOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  CameraAltOutlined,
  LocationSearching as LocationSearchingIcon,
  Place as PlaceOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  Close as CloseIcon,
  ListAlt as BreakdownIcon,
  Dvr as DailiesIcon,
  Info,
  Info as OverviewIcon,
  EventNote,
  FlashOn,
  Schedule as ScheduleIcon, // Define ScheduleIcon
  TheaterComedy as TheaterComedyIcon // Define TheaterComedyIcon
} from '@mui/icons-material';
import {
  AttachMoney as MoneyIcon,
  School,
  PersonOutline as AdvisorIcon,
  Payments as PaymentsIcon
} from '@mui/icons-material';
import GanttChart from '../components/GanttChart';
import Storyboard from '../components/Storyboard';
import ProjectCalendar from '../components/FullProjectCalendar';
import CallsheetMain from '../components/callsheet/CallsheetMain';
import OneLiner from '../components/OneLiner';
import ScriptManager from '../components/ScriptManager';
import ScriptUploader from '../components/ScriptUploader';
import KanbanBoard from '../components/KanbanBoard';
import TaskForm from '../components/TaskForm';
import Dailies from '../components/Dailies';
import Budget from '../components/Budget';
import Payroll from './Payroll';
import Resources from './Resources'; // Update the import path
import StoryOutline from '../components/StoryOutline';
import * as kanTaskService from '../services/kanTaskService';
import * as ganttTaskService from '../services/ganttTaskService';
import CrewManagement from '../components/CrewManagement';
import Cast from './Cast'; // Update the import path
import Breakdown from '../components/Breakdown'; // Import the new Breakdown component
import Schedule from './Schedule'; // Import the new Schedule component
import { getProjectCrew, addCrewMember as addCrewMemberService, removeCrewMember as removeCrewMemberService } from '../services/crewService';
import { getBudgetSummary, formatCurrency } from '../services/budgetService';
import { PermissionsProvider, usePermissions } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002';

function TabPanel({ children, value, index, isWide = false, allowScroll = false }) {
  return (
    <div 
      hidden={value !== index} 
      style={{ 
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        padding: 0, 
        height: '100%',
        overflow: allowScroll ? 'auto' : 'hidden', 
        width: value === index && isWide ? 'max-content' : '100%',
        minWidth: '100%'
      }}
    >
      {children}
    </div>
  );
}

// Tab wrapper that disables based on a permission key
function GatedTab({ perm, tooltip, ...tabProps }) {
  const { can } = usePermissions();
  const disabled = perm ? !can(perm) : false;
  return (
    <Tooltip title={tooltip || ''}>
      <span>
        <Tab disabled={disabled} {...tabProps} />
      </span>
    </Tooltip>
  );
}

// Function to get color based on project phase
const getPhaseColor = (phase) => {
  switch (phase?.toLowerCase()) {
    case 'development':
      return '#FF9800'; // Yellow/Orange
    case 'pre-production':
      return '#4CAF50'; // Green
    case 'production':
      return '#2196F3'; // Blue
    case 'post-production':
      return '#9C27B0'; // Purple
    case 'distribution':
      return '#009688'; // Teal
    default:
      return '#757575'; // Grey
  }
};

const ProjectDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [storyOutlineWide, setStoryOutlineWide] = useState(false);
  const [project, setProject] = useState(null);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessages, setErrorMessages] = useState({
    kanTasks: '',
    ganttTasks: '',
    general: '',
    crew: ''
  });
  
  // KanTask state
  const [kanTasks, setKanTasks] = useState({
    pending: [],
    'in-progress': [],
    review: [],
    completed: []
  });

  // Gantt state - separate from Kanban
  const [ganttTasks, setGanttTasks] = useState([]);
  
  // Loading state for async operations
  const [isLoading, setIsLoading] = useState(false);
  
  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Production phases configuration
  const [productionPhases, setProductionPhases] = useState([
    {
      id: 'development',
      name: 'Development',
      color: '#FF9800', // Yellow/Orange
      order: 1
    },
    {
      id: 'pre-production',
      name: 'Pre-Production',
      color: '#4CAF50', // Green
      order: 2
    },
    {
      id: 'production',
      name: 'Production',
      color: '#2196F3', // Blue
      order: 3
    },
    {
      id: 'post-production',
      name: 'Post-Production',
      color: '#9C27B0', // Purple
      order: 4
    },
    {
      id: 'distribution',
      name: 'Distribution',
      color: '#009688', // Teal
      order: 5
    }
  ]);

  const [crewMembers, setCrewMembers] = useState([]);

  const [script, setScript] = useState(null);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [editedProject, setEditedProject] = useState({});

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // UI state: synopsis expansion
  const [showFullSynopsis, setShowFullSynopsis] = useState(false);

  // Dev-only guards to prevent duplicate fetches under React 18 StrictMode
  const didFetchProjectRef = useRef(false);
  const didFetchKanTasksRef = useRef(false);
  const didFetchGanttRef = useRef(false);
  const didFetchCrewRef = useRef(false);

  const summaryBudgetValue = budgetSummary?.budget?.totals?.grandTotal ?? null;
  const budgetCurrency = budgetSummary?.budget?.currency || 'USD';
  const hasBudgetSummary = summaryBudgetValue !== null && summaryBudgetValue !== undefined;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    // Handle file upload logic here
    console.log('Uploading file:', file.name);
  };

  const handleUploadComplete = (newScript) => {
    console.log('Script uploaded successfully:', newScript);
    setScript(newScript);
    setUploadDialogOpen(false);
  };

  const onKanTasksUpdate = (updatedTasks) => {
    setKanTasks(updatedTasks);
  };

  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      try {
        // Try to fetch project (will check both owner/collaborator and crew access)
        const response = await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch project');
        }
        
        const data = await response.json();
        console.log('Project data:', data);
        setProject(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching project:', error);
        setErrorMessages(prev => ({ 
          ...prev, 
          general: error.message || 'Failed to load project details'
        }));
        setLoading(false);
      }
    };

    if (projectId) {
      if (didFetchProjectRef.current) return; // guard duplicate in dev
      didFetchProjectRef.current = true;
      fetchProject();
    }
  }, [projectId]);

  const loadBudgetSummary = React.useCallback(async () => {
    if (!projectId) return;
    try {
      const summary = await getBudgetSummary(projectId);
      setBudgetSummary(summary?.exists ? summary : null);
    } catch (err) {
      console.error('Error fetching budget summary:', err);
      setBudgetSummary(null);
    }
  }, [projectId]);

  useEffect(() => {
    loadBudgetSummary();
  }, [loadBudgetSummary]);

  useEffect(() => {
    if (!editProjectDialogOpen || !hasBudgetSummary) return;
    setEditedProject(prev => ({ ...prev, budget: summaryBudgetValue }));
  }, [editProjectDialogOpen, hasBudgetSummary, summaryBudgetValue]);

  // Fetch KanTasks
  useEffect(() => {
    const fetchKanTasks = async () => {
      try {
        const tasks = await kanTaskService.getProjectTasks(projectId);
        setKanTasks(tasks);
      } catch (error) {
        console.error('Error fetching KanTasks:', error);
        setErrorMessages(prev => ({ ...prev, kanTasks: 'Failed to load Kanban tasks' }));
      }
    };

    if (projectId) {
      if (didFetchKanTasksRef.current) return; // guard duplicate in dev
      didFetchKanTasksRef.current = true;
      fetchKanTasks();
    }
  }, [projectId]);

  // Fetch Gantt Tasks - separate from Kanban
  useEffect(() => {
    const fetchGanttTasks = async () => {
      try {
        const tasks = await ganttTaskService.getProjectGanttTasks(projectId);
        setGanttTasks(tasks);
      } catch (error) {
        console.error('Error fetching Gantt tasks:', error);
        setErrorMessages(prev => ({ ...prev, ganttTasks: 'Failed to load Gantt tasks' }));
      }
    };

    if (projectId) {
      fetchGanttTasks();
    }
  }, [projectId]);

  // Fetch crew members (sequence after project is loaded, guarded for StrictMode)
  useEffect(() => {
    const fetchCrewMembers = async () => {
      if (!projectId || !project) return; // ensure project details loaded first
      try {
        console.log('Fetching crew members for project:', projectId);
        const crew = await getProjectCrew(projectId);
        setCrewMembers(crew);
      } catch (error) {
        console.error('Error fetching crew members:', error);
        setErrorMessages(prev => ({
          ...prev,
          crew: 'Failed to load crew members'
        }));
      }
    };

    if (!projectId || !project) return; // wait for project data
    if (didFetchCrewRef.current) return; // guard duplicate in dev
    didFetchCrewRef.current = true;
    fetchCrewMembers();
  }, [projectId, project]);

  const handleEditProjectClick = () => {
    if (!project) return;
    setEditedProject({
      projectName: project.projectName,
      logline: project.logline,
      synopsis: project.synopsis,
      genre: project.genre,
      phase: project.phase,
      budget: hasBudgetSummary ? summaryBudgetValue : project.budget,
      teamSize: project.teamSize,
      startDate: project.startDate,
      endDate: project.endDate,
      academicInfo: project.academicInfo || {}
    });
    setEditProjectDialogOpen(true);
  };

  const handleUpdateProject = async () => {
    try {
      console.log('Updating project with ID:', projectId);
      console.log('Project data being sent:', editedProject);
      
      // Validate projectId before sending request
      if (!projectId || projectId.trim() === '') {
        setNotification({
          open: true,
          message: 'Invalid project ID',
          severity: 'error'
        });
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editedProject)
      });

      // Handle different error status codes
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to update project';
        
        if (response.status === 404) {
          throw new Error(`Project not found: ${errorMessage}`);
        } else if (response.status === 403) {
          throw new Error(`Permission denied: ${errorMessage}`);
        } else if (response.status === 400) {
          throw new Error(`Invalid request: ${errorMessage}`);
        } else {
          throw new Error(`Server error (${response.status}): ${errorMessage}`);
        }
      }

      const updatedProject = await response.json();
      setProject(updatedProject);
      setEditProjectDialogOpen(false);
      
      setNotification({
        open: true,
        message: 'Project updated successfully',
        severity: 'success'
      });

      // Add activity
      try {
        await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}/activities`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            description: 'Project details updated',
            activityType: 'project_update'
          })
        });
      } catch (activityError) {
        // Don't fail the whole operation if just the activity logging fails
        console.warn('Failed to log project update activity:', activityError);
      }
    } catch (error) {
      console.error('Error updating project:', error);
      setErrorMessages(prev => ({ ...prev, general: error.message || 'Failed to update project' }));
      
      setNotification({
        open: true,
        message: error.message || 'Failed to update project',
        severity: 'error'
      });
    }
  };

  // Genre options for the dropdown
  const genres = [
    'Action',
    'Adventure',
    'Animation',
    'Comedy',
    'Crime',
    'Documentary',
    'Drama',
    'Fantasy',
    'Horror',
    'Musical',
    'Mystery',
    'Romance',
    'Science Fiction',
    'Thriller',
    'Western',
    'Other'
  ];

  // Project phases for the dropdown
  const phases = [
    'Development',
    'Pre-Production',
    'Production',
    'Post-Production',
    'Distribution'
  ];

  const handleAddCrewMember = async (newMember) => {
    try {
      console.log('Adding crew member to state:', newMember);
      
      // Update the state immediately with the new crew member
      setCrewMembers(prevMembers => [...prevMembers, newMember]);

      setNotification({
        open: true,
        message: newMember.status === 'pending' ? 'Invitation sent successfully' : 'Crew member added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding crew member:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to add crew member',
        severity: 'error'
      });
    }
  };

  const handleDeleteCrewMember = async (memberId, isInvitation) => {
    try {
      console.log('Removing crew member from dashboard state:', memberId);
      
      // Just update the UI state since the API call was already made by CrewManagement
      setCrewMembers(prev => prev.filter(member => member._id !== memberId));
      
      setNotification({
        open: true,
        message: isInvitation ? 'Invitation cancelled successfully' : 'Crew member removed successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating crew state:', error);
      
      // If there's an error, refresh the crew list to ensure consistency
      try {
        console.log('Refreshing crew list after error');
        const updatedCrew = await getProjectCrew(projectId);
        setCrewMembers(updatedCrew);
      } catch (refreshError) {
        console.error('Error refreshing crew list:', refreshError);
        setNotification({
          open: true,
          message: 'Failed to refresh crew list',
          severity: 'error'
        });
      }
    }
  };

  const renderKanbanBoard = () => (
    <Box sx={{ p: 2, width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Kanban Board</Typography>
      </Box>
      {errorMessages.kanTasks && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessages.kanTasks}
        </Alert>
      )}
      <KanbanBoard
        projectId={projectId}
        tasks={kanTasks}
        onTasksUpdate={onKanTasksUpdate}
        teamMembers={crewMembers}
      />
    </Box>
  );
  const renderCrewTab = () => (
    <Box sx={{ p: 2, width: '100%' }}>
      <CrewManagement
        projectId={projectId}
        crewMembers={crewMembers}
        onAddCrewMember={handleAddCrewMember}
        onDeleteCrewMember={handleDeleteCrewMember}
      />
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading project details...</Typography>
      </Box>
    );
  }

  if (errorMessages.general) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error">{errorMessages.general}</Typography>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Project not found</Typography>
      </Box>
    );
  }

  return (
    <PermissionsProvider projectId={projectId} currentUserId={user?._id || user?.id}>
    <Box 
      sx={{
        width: tabValue === 2 && storyOutlineWide ? 'max-content' : '100%',
        minWidth: '100%',
        height: '100%',
        minHeight: '100%',
        overflow: tabValue === 2 && storyOutlineWide ? 'visible' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 0
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0, pl: 2, flexShrink: 0 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="scrollable" 
          scrollButtons="auto" 
          aria-label="project navigation tabs"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              padding: '8px 4px',
              margin: '0 4px',
              flexDirection: 'column',
              fontSize: '0.7rem',
              textTransform: 'none'  // This prevents uppercase transformation
            }
          }}
        >
          <Tooltip title="Overview"><Tab icon={<OverviewIcon />} /></Tooltip>
          <GatedTab perm="development.view" tooltip="Story Development" icon={<OutlineIcon />} />
          <GatedTab perm="scripts.view" tooltip="Script Management" icon={<ScriptIcon />} />
          <GatedTab perm="storyboards.view" tooltip="Storyboard Editor" icon={<StoryboardIcon />} />
          <GatedTab perm="cast.view" tooltip="Cast Management" icon={<TheaterComedyIcon />} />
          <GatedTab perm="crew.view" tooltip="Crew Management" icon={<GroupIcon />} />
          <GatedTab perm="tasks.view" tooltip="Task Management" icon={<TasksIcon />} />
          <GatedTab perm="calendar.view" tooltip="Project Calendar" icon={<CalendarIcon />} />
          <GatedTab perm="schedules.view" tooltip="Production Schedule" icon={<ScheduleIcon />} />
          <GatedTab perm="callsheets.view" tooltip="Call Sheets" icon={<EventNote />} />
          <GatedTab perm="resources.view" tooltip="Project Resources" icon={<ResourcesIcon />} />
          <GatedTab perm="dailies.view" tooltip="Dailies Footage" icon={<DailiesIcon />} />
          <GatedTab perm="budget.view" tooltip="Budget & Finance" icon={<MoneyIcon />} />
          {project?.phase?.toLowerCase() !== 'development' && (
            <GatedTab tooltip="Payroll" icon={<PaymentsIcon />} />
          )}
        </Tabs>
      </Box>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0} allowScroll={true}>
        <Container maxWidth={false} disableGutters sx={{ py: 2, pl: 0, pr: 2 }}>
          <Grid container spacing={2} sx={{ 
            width: '100%',
            height: '100%',
            margin: '0',
            overflow: 'auto'
          }}>
            {/* Project Header */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  mb: 3,
                  borderRadius: 2,
                  background: (theme) => theme.palette.background.paper,
                  color: (theme) => theme.palette.text.primary,
                  border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: (theme) => theme.palette.mode === 'dark' 
                    ? '0 2px 8px rgba(0,0,0,0.15)' 
                    : '0 1px 3px rgba(0,0,0,0.04)'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 2.5, sm: 0 }
                }}>
                  <Box>
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                      {(() => {
                        const phaseColor = getPhaseColor(project.phase);
                        return (
                          <>
                            <Chip 
                              label={project.projectType?.toUpperCase() || 'FILM'}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                color: 'text.secondary',
                                borderColor: (theme) => alpha(theme.palette.divider, 0.2),
                                backgroundColor: 'transparent',
                                fontWeight: 500,
                                fontSize: '0.7rem'
                              }}
                            />
                            <Chip 
                              label={project.phase || 'DEVELOPMENT'}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                color: phaseColor,
                                borderColor: alpha(phaseColor || theme.palette.primary.main, 0.3),
                                backgroundColor: alpha(phaseColor || theme.palette.primary.main, 0.04),
                                fontWeight: 500,
                                fontSize: '0.7rem'
                              }}
                            />
                            {project.genre && (
                              <Chip 
                                label={project.genre.toUpperCase()}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  color: 'text.secondary',
                                  borderColor: (theme) => alpha(theme.palette.divider, 0.2),
                                  backgroundColor: 'transparent',
                                  fontWeight: 500,
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                          </>
                        );
                      })()}
                    </Box>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1, 
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {project.projectName}
                    </Typography>
                    {project.logline && (
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'text.secondary', 
                          maxWidth: '800px', 
                          fontSize: '0.9rem', 
                          lineHeight: 1.6
                        }}
                      >
                        {project.logline}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEditProjectClick}
                      sx={{ 
                        color: 'text.primary',
                        borderColor: (theme) => alpha(theme.palette.divider, 0.2),
                        textTransform: 'none',
                        px: 2,
                        py: 0.75,
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      Edit Project
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Main Content Area */}
            <Grid item xs={12} md={8}>
              {/* Project Progress Section */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 2.5, md: 3 }, 
                  mb: 4, 
                  borderRadius: 2, 
                  boxShadow: (theme) => theme.palette.mode === 'dark' 
                    ? '0 2px 8px rgba(0,0,0,0.15)' 
                    : '0 1px 3px rgba(0,0,0,0.04)',
                  border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3, 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1rem'
                  }}
                >
                  Project Progress
                </Typography>
                <Grid container spacing={2}>
                  {/* Phase Progress */}
                  <Grid item xs={12}>
                    <Box 
                      sx={{ 
                        mb: 3,
                        p: 2, 
                        borderRadius: 1.5, 
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <TimelineOutlinedIcon sx={{ fontSize: '1rem', opacity: 0.6 }} />
                          Production Phase
                        </Typography>
                        <Chip
                          label={project.phase || 'Development'}
                          size="small"
                          sx={{ 
                            fontWeight: 500,
                            borderRadius: 1,
                            bgcolor: alpha(getPhaseColor(project.phase), 0.08),
                            color: getPhaseColor(project.phase),
                            border: (theme) => `1px solid ${alpha(getPhaseColor(project.phase) || theme.palette.primary.main, 0.2)}`,
                            height: 22,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {productionPhases.map((phase, index) => {
                          const isActive = index <= productionPhases.findIndex(p => p.id === project.phase?.toLowerCase());
                          return (
                            <Tooltip key={phase.id} title={phase.name} enterDelay={400}>
                              <Box
                                sx={{
                                  height: 4,
                                  flex: 1,
                                  bgcolor: isActive ? phase.color : alpha(theme.palette.divider, 0.15),
                                  borderRadius: 2
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.25 }}>
                        {productionPhases.map((phase, index) => (
                          <Typography 
                            key={phase.id} 
                            variant="caption" 
                            sx={{ 
                              color: index <= productionPhases.findIndex(p => p.id === project.phase?.toLowerCase())
                                ? phase.color
                                : 'text.disabled',
                              fontWeight: index <= productionPhases.findIndex(p => p.id === project.phase?.toLowerCase())
                                ? 500
                                : 400,
                              fontSize: '0.65rem',
                              flex: 1,
                              textAlign: index === 0 ? 'left' : index === productionPhases.length - 1 ? 'right' : 'center'
                            }}
                          >
                            {phase.name}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Key Metrics */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        height: '100%',
                        borderRadius: 2,
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        background: (theme) => theme.palette.mode === 'dark' 
                          ? alpha('#2196F3', 0.04)
                          : alpha('#2196F3', 0.02),
                        display: 'flex', 
                        flexDirection: 'column', 
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        boxShadow: (theme) => theme.palette.mode === 'dark'
                          ? '0 2px 8px rgba(0,0,0,0.15)'
                          : '0 1px 3px rgba(0,0,0,0.04)',
                        '&:hover': {
                          boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 4px 12px rgba(0,0,0,0.2)'
                            : '0 2px 6px rgba(0,0,0,0.06)'
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: 3,
                          height: '100%',
                          bgcolor: '#2196F3'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pl: 1 }}>
                        <Box sx={{ 
                          bgcolor: (theme) => alpha('#2196F3', 0.1),
                          color: '#2196F3',
                          p: 0.75, 
                          borderRadius: 1.5,
                          mr: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <TasksIcon sx={{ fontSize: '1.1rem' }} />
                        </Box>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: 'text.primary'
                        }}>
                          Tasks Progress
                        </Typography>
                      </Box>
                      
                      <Box sx={{ pl: 1, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography 
                            variant="h3" 
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: '2rem', 
                              lineHeight: 1,
                              letterSpacing: '-0.02em',
                              color: '#2196F3'
                            }}
                          >
                            {Object.values(kanTasks).flat().length > 0 
                              ? Math.round((kanTasks.completed?.length || 0) / Object.values(kanTasks).flat().length * 100) 
                              : 0}
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600, 
                              color: alpha('#2196F3', 0.7),
                              fontSize: '1rem'
                            }}
                          >
                            %
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mt: 'auto', pl: 1 }}>
                        <Box sx={{ 
                          height: 3, 
                          bgcolor: (theme) => alpha('#2196F3', 0.1), 
                          borderRadius: 1.5, 
                          mb: 1.25, 
                          width: '100%', 
                          overflow: 'hidden' 
                        }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              width: `${Object.values(kanTasks).flat().length > 0 
                                ? Math.round((kanTasks.completed?.length || 0) / Object.values(kanTasks).flat().length * 100) 
                                : 0}%`,
                              borderRadius: 1.5,
                              bgcolor: '#2196F3'
                            }} 
                          />
                        </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500, 
                            fontSize: '0.8rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            color: 'text.secondary'
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: '0.85rem', color: '#2196F3', opacity: 0.8 }} />
                          {kanTasks.completed?.length || 0} of {Object.values(kanTasks).flat().length} tasks completed
                        </Typography>
                        {Object.values(kanTasks).flat().length === 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              No tasks yet — get started by creating your first task.
                            </Typography>
                            <Button size="small" sx={{ ml: 1 }} onClick={() => setTabValue(4)}>
                              Add Task
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        height: '100%',
                        borderRadius: 2,
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        background: (theme) => theme.palette.mode === 'dark' 
                          ? alpha('#4CAF50', 0.04)
                          : alpha('#4CAF50', 0.02),
                        display: 'flex', 
                        flexDirection: 'column', 
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        boxShadow: (theme) => theme.palette.mode === 'dark'
                          ? '0 2px 8px rgba(0,0,0,0.15)'
                          : '0 1px 3px rgba(0,0,0,0.04)',
                        '&:hover': {
                          boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 4px 12px rgba(0,0,0,0.2)'
                            : '0 2px 6px rgba(0,0,0,0.06)'
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: 3,
                          height: '100%',
                          bgcolor: '#4CAF50'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pl: 1 }}>
                        <Box sx={{ 
                          bgcolor: (theme) => alpha('#4CAF50', 0.1),
                          color: '#4CAF50',
                          p: 0.75, 
                          borderRadius: 1.5,
                          mr: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <GroupIcon sx={{ fontSize: '1.1rem' }} />
                        </Box>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: 'text.primary'
                        }}>
                          Team Size
                        </Typography>
                      </Box>
                      
                      <Box sx={{ pl: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            fontWeight: 700, 
                            fontSize: '2rem', 
                            lineHeight: 1,
                            letterSpacing: '-0.02em',
                            color: '#4CAF50'
                          }}
                        >
                          {crewMembers.length}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mt: 'auto', pl: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.25 }}>
                          {[...Array(Math.min(5, crewMembers.length))].map((_, i) => (
                            <Avatar 
                              key={i} 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                fontSize: '0.7rem', 
                                bgcolor: alpha('#4CAF50', 0.1),
                                color: '#4CAF50',
                                border: (theme) => `1px solid ${alpha('#4CAF50', 0.2)}`
                              }}
                            >
                              {crewMembers[i]?.name?.charAt(0) || 'C'}
                            </Avatar>
                          ))}
                          {crewMembers.length > 5 && (
                            <Avatar 
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                fontSize: '0.65rem', 
                                bgcolor: alpha('#4CAF50', 0.1),
                                color: '#4CAF50',
                                border: (theme) => `1px solid ${alpha('#4CAF50', 0.2)}`
                              }}
                            >
                              +{crewMembers.length - 5}
                            </Avatar>
                          )}
                        </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500, 
                            fontSize: '0.8rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            color: 'text.secondary'
                          }}
                        >
                          <PeopleIcon sx={{ fontSize: '0.85rem', color: '#4CAF50', opacity: 0.8 }} />
                          {crewMembers.length} Crew Members
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        height: '100%',
                        borderRadius: 2,
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        background: (theme) => theme.palette.mode === 'dark' 
                          ? alpha('#9C27B0', 0.04)
                          : alpha('#9C27B0', 0.02),
                        display: 'flex', 
                        flexDirection: 'column', 
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease',
                        boxShadow: (theme) => theme.palette.mode === 'dark'
                          ? '0 2px 8px rgba(0,0,0,0.15)'
                          : '0 1px 3px rgba(0,0,0,0.04)',
                        '&:hover': {
                          boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 4px 12px rgba(0,0,0,0.2)'
                            : '0 2px 6px rgba(0,0,0,0.06)'
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: 3,
                          height: '100%',
                          bgcolor: '#9C27B0'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pl: 1 }}>
                        <Box sx={{ 
                          bgcolor: (theme) => alpha('#9C27B0', 0.1),
                          color: '#9C27B0',
                          p: 0.75, 
                          borderRadius: 1.5,
                          mr: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <CalendarIcon sx={{ fontSize: '1.1rem' }} />
                        </Box>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          color: 'text.primary'
                        }}>
                          Timeline
                        </Typography>
                      </Box>
                      
                      {/* Days Remaining */}
                      <Box sx={{ pl: 1, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography 
                            variant="h3" 
                            sx={{ 
                              fontWeight: 700, 
                              fontSize: '2rem', 
                              lineHeight: 1,
                              letterSpacing: '-0.02em',
                              color: '#9C27B0'
                            }}
                          >
                            {project.endDate ? Math.max(0, Math.ceil((new Date(project.endDate) - new Date()) / (1000 * 60 * 60 * 24))) : 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            days left
                          </Typography>
                        </Box>
                        {project.startDate && project.endDate && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              mt: 0.5,
                              color: alpha('#9C27B0', 0.7),
                              fontWeight: 500
                            }}
                          >
                            of {Math.ceil((new Date(project.endDate) - new Date(project.startDate)) / (1000 * 60 * 60 * 24))} total days
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Gantt Task Progress */}
                      {ganttTasks && ganttTasks.length > 0 && (
                        <Box sx={{ pl: 1, mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Schedule Progress
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#9C27B0' }}>
                              {Math.round(ganttTasks.reduce((acc, t) => acc + (t.progress || 0), 0) / ganttTasks.length)}%
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            height: 4, 
                            borderRadius: 2, 
                            bgcolor: alpha('#9C27B0', 0.1),
                            overflow: 'hidden'
                          }}>
                            <Box sx={{ 
                              height: '100%', 
                              borderRadius: 2,
                              width: `${Math.round(ganttTasks.reduce((acc, t) => acc + (t.progress || 0), 0) / ganttTasks.length)}%`,
                              bgcolor: '#9C27B0'
                            }} />
                          </Box>
                        </Box>
                      )}
                      
                      {/* Milestones */}
                      {ganttTasks && ganttTasks.filter(t => t.isMilestone === true).length > 0 && (
                        <Box sx={{ pl: 1, mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                              bgcolor: alpha('#9C27B0', 0.1), 
                              p: 0.5, 
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <FlagIcon sx={{ fontSize: '0.85rem', color: '#9C27B0' }} />
                            </Box>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {ganttTasks.filter(t => t.isMilestone === true).length} Milestones
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {ganttTasks.filter(t => t.isMilestone === true && new Date(t.start) > new Date()).length} upcoming
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                      
                      <Box sx={{ mt: 'auto', pl: 1 }}>
                        {project.startDate && project.endDate && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontWeight: 500, 
                                color: alpha('#9C27B0', 0.8),
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                bgcolor: alpha('#9C27B0', 0.05)
                              }}
                            >
                              {new Date(project.startDate).toLocaleDateString()}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontWeight: 500, 
                                color: alpha('#9C27B0', 0.8),
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                bgcolor: alpha('#9C27B0', 0.05)
                              }}
                            >
                              {new Date(project.endDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500, 
                            fontSize: '0.8rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            color: 'text.secondary'
                          }}
                        >
                          <EventIcon sx={{ fontSize: '0.85rem', color: '#9C27B0', opacity: 0.8 }} />
                          {project.endDate ? 
                            `${Math.max(0, Math.ceil((new Date(project.endDate) - new Date()) / (1000 * 60 * 60 * 24)))} Days Remaining` : 
                            'Timeline Not Set'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>

              {/* Synopsis Section */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 2.5, md: 3 }, 
                  mb: 4, 
                  borderRadius: 2, 
                  border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  background: (theme) => theme.palette.background.paper,
                  boxShadow: (theme) => theme.palette.mode === 'dark' 
                    ? '0 2px 8px rgba(0,0,0,0.15)' 
                    : '0 1px 3px rgba(0,0,0,0.04)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2.5, 
                    fontWeight: 600, 
                    fontSize: '1rem',
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box sx={{ 
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    p: 0.75,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Info sx={{ fontSize: '1.1rem' }} />
                  </Box>
                  Synopsis
                  <IconButton aria-label="edit synopsis" onClick={() => setEditProjectDialogOpen(true)} sx={{ ml: 'auto' }}>
                    <EditIcon />
                  </IconButton>
                </Typography>
                
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 1.5, 
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {project.synopsis ? (
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-line', 
                          lineHeight: 1.7, 
                          color: 'text.primary', 
                          fontSize: '0.9rem', 
                          letterSpacing: '0.01em',
                          display: '-webkit-box',
                          WebkitLineClamp: showFullSynopsis ? 'unset' : 6,
                          WebkitBoxOrient: 'vertical',
                          overflow: showFullSynopsis ? 'visible' : 'hidden'
                        }}
                      >
                        {project.synopsis}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                        <Button size="small" onClick={() => setShowFullSynopsis(v => !v)}>
                          {showFullSynopsis ? 'Collapse' : 'Read more'}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <DescriptionOutlinedIcon sx={{ fontSize: '1.5rem', color: 'text.disabled', mb: 1 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontStyle: 'italic', 
                          color: 'text.secondary',
                          mb: 2
                        }}
                      >
                        No synopsis available. Add a synopsis to provide an overview of your project.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => setEditProjectDialogOpen(true)}
                        sx={{ 
                          borderRadius: 1, 
                          textTransform: 'none',
                          px: 1.5,
                          fontWeight: 500
                        }}
                      >
                        Add Synopsis
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              {/* Project Details Card */}
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                mb: 3, 
                borderRadius: 2, 
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: (theme) => theme.palette.background.paper,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                boxShadow: (theme) => theme.palette.mode === 'dark' 
                  ? '0 2px 8px rgba(0,0,0,0.15)' 
                  : '0 1px 3px rgba(0,0,0,0.04)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    bgcolor: (theme) => alpha('#E91E63', 0.1), 
                    p: 0.75, 
                    borderRadius: 1.5, 
                    mr: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <OutlineIcon sx={{ color: '#E91E63', fontSize: '1.1rem' }} />
                  </Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary'
                  }}>
                    Project Details
                  </Typography>
                </Box>
                <List disablePadding sx={{ 
                  '& .MuiListItem-root': { 
                    py: 1.25, 
                    px: 0.5, 
                    borderRadius: 1, 
                    transition: 'all 0.15s ease',
                    '&:hover': { 
                      bgcolor: (theme) => alpha(theme.palette.action.hover, 0.04)
                    } 
                  } 
                }}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                          Genre
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {project.genre || 'Not specified'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                          Budget
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {budgetSummary?.budget?.totals?.grandTotal
                              ? formatCurrency(budgetSummary.budget.totals.grandTotal, budgetSummary.budget.currency)
                              : (project.budget
                                ? formatCurrency(typeof project.budget === 'number' ? project.budget : parseFloat(String(project.budget).replace(/[^0-9.]/g, '')) || 0)
                                : 'Budget not set')}
                          </Typography>
                          {budgetSummary?.budget?.totals?.actualSpent !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              {`Actual: ${formatCurrency(budgetSummary.budget.totals.actualSpent, budgetSummary.budget.currency)}`}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                          Start Date
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {project.startDate ? new Date(project.startDate).toLocaleDateString() : 'Not set'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                          Target End Date
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {project.endDate ? new Date(project.endDate).toLocaleDateString() : 'Not set'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {project.projectType !== 'professional' && (
                    <>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          disableTypography
                          primary={
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                              Institution
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                              {project.academicInfo?.institution || 'Not specified'}
                            </Typography>
                          }
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          disableTypography
                          primary={
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                              Advisor
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                              {project.academicInfo?.advisor || 'Not specified'}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </Paper>

              {/* Quick Actions */}
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2, 
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: (theme) => theme.palette.background.paper,
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                boxShadow: (theme) => theme.palette.mode === 'dark' 
                  ? '0 2px 8px rgba(0,0,0,0.15)' 
                  : '0 1px 3px rgba(0,0,0,0.04)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ 
                    bgcolor: (theme) => alpha('#FF9800', 0.1), 
                    p: 0.75, 
                    borderRadius: 1.5, 
                    mr: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FlashOn sx={{ color: '#FF9800', fontSize: '1.1rem' }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Quick Actions
                  </Typography>
                </Box>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Tooltip title="Create a new task in the Kanban board" enterDelay={400}>
                      <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setTabValue(6)}
                      sx={{ 
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 1,
                        fontSize: '0.85rem'
                      }}
                      aria-label="add task"
                    >
                      Add Task
                    </Button>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6}>
                    <Tooltip title="Upload a new script to the project" enterDelay={400}>
                      <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => setTabValue(2)}
                      sx={{ 
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 1,
                        fontSize: '0.85rem'
                      }}
                      aria-label="upload script"
                    >
                      Upload Script
                    </Button>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6}>
                    <Tooltip title="Manage cast members and characters" enterDelay={400}>
                      <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<TheaterComedyIcon />}
                      onClick={() => setTabValue(4)}
                      sx={{ 
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 1,
                        fontSize: '0.85rem'
                      }}
                      aria-label="manage cast"
                    >
                      Manage Cast
                    </Button>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6}>
                    <Tooltip title="Manage crew members and roles" enterDelay={400}>
                      <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<GroupIcon />}
                      onClick={() => setTabValue(5)}
                      sx={{ 
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 1,
                        fontSize: '0.85rem'
                      }}
                      aria-label="manage crew"
                    >
                      Manage Crew
                    </Button>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </TabPanel>

      {/* Edit Project Dialog */}
      <Dialog 
        open={editProjectDialogOpen} 
        onClose={() => setEditProjectDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit Project</Typography>
            <IconButton onClick={() => setEditProjectDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Name"
                value={editedProject.projectName || ''}
                onChange={(e) => setEditedProject({ ...editedProject, projectName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logline"
                value={editedProject.logline || ''}
                onChange={(e) => setEditedProject({ ...editedProject, logline: e.target.value })}
                multiline
                rows={2}
                helperText="A one-sentence summary of your project"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Synopsis"
                value={editedProject.synopsis || ''}
                onChange={(e) => setEditedProject({ ...editedProject, synopsis: e.target.value })}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Genre"
                value={editedProject.genre || ''}
                onChange={(e) => setEditedProject({ ...editedProject, genre: e.target.value })}
                required
              >
                {genres.map((genre) => (
                  <MenuItem key={genre} value={genre}>
                    {genre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Project Phase"
                value={editedProject.phase || ''}
                onChange={(e) => setEditedProject({ ...editedProject, phase: e.target.value })}
              >
                {phases.map((phase) => (
                  <MenuItem key={phase} value={phase}>
                    {phase}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Budget"
                value={hasBudgetSummary ? formatCurrency(summaryBudgetValue, budgetCurrency) : (editedProject.budget || '')}
                onChange={(e) => setEditedProject({ ...editedProject, budget: e.target.value })}
                type={hasBudgetSummary ? 'text' : 'number'}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  readOnly: hasBudgetSummary
                }}
                helperText={hasBudgetSummary ? 'Managed via Budget workspace' : 'Enter a quick estimate if no budget exists yet'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Team Size"
                value={editedProject.teamSize || ''}
                onChange={(e) => setEditedProject({ ...editedProject, teamSize: e.target.value })}
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={editedProject.startDate?.split('T')[0] || ''}
                onChange={(e) => setEditedProject({ ...editedProject, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Target End Date"
                value={editedProject.endDate?.split('T')[0] || ''}
                onChange={(e) => setEditedProject({ ...editedProject, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {project.projectType !== 'professional' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Institution"
                    value={editedProject.academicInfo?.institution || ''}
                    onChange={(e) => setEditedProject({
                      ...editedProject,
                      academicInfo: {
                        ...editedProject.academicInfo,
                        institution: e.target.value
                      }
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Advisor"
                    value={editedProject.academicInfo?.advisor || ''}
                    onChange={(e) => setEditedProject({
                      ...editedProject,
                      academicInfo: {
                        ...editedProject.academicInfo,
                        advisor: e.target.value
                      }
                    })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProjectDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateProject} 
            variant="contained" 
            color="primary"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Script Tab */}
      <TabPanel value={tabValue} index={2} allowScroll={true}>
        <Container maxWidth={false} disableGutters sx={{ py: 2, px: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ScriptManager projectId={projectId} projectName={project?.projectName || ''} />
            </Grid>
          </Grid>
        </Container>
      </TabPanel>

      {/* Development Tab */}
      <TabPanel value={tabValue} index={1} isWide={storyOutlineWide}>
        <StoryOutline
          projectId={projectId}
          projectName={project?.projectName || ''}
          projectLogline={project?.logline || ''}
          projectSynopsis={project?.synopsis || ''}
          onWidthModeChange={setStoryOutlineWide}
          wideMode={storyOutlineWide}
        />
      </TabPanel>

      {/* Storyboard Tab */}
      <TabPanel value={tabValue} index={3}>
        <Storyboard projectId={projectId} />
      </TabPanel>

      {/* Tasks Tab */}
      <TabPanel value={tabValue} index={6}>
        <Box sx={{ width: '100%' }}>
          {errorMessages.kanTasks && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessages.kanTasks}
            </Alert>
          )}
          <KanbanBoard
            projectId={projectId}
            tasks={kanTasks}
            onTasksUpdate={onKanTasksUpdate}
            teamMembers={crewMembers}
          />
        </Box>
      </TabPanel>

      {/* Cast Tab */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ p: 2 }}>
          <Cast projectId={projectId} />
        </Box>
      </TabPanel>

      {/* Crew Tab */}
      <TabPanel value={tabValue} index={5}>
        {renderCrewTab()}
      </TabPanel>

      {/* Calendar Tab */}
      <TabPanel value={tabValue} index={7}>
        <ProjectCalendar 
          projectId={projectId} 
          crewMembers={crewMembers}
          projectOwnerId={project?.owner?._id || project?.owner}
        />
      </TabPanel>

      {/* Schedule Tab */}
      <TabPanel value={tabValue} index={8} allowScroll={true}>
        <Schedule projectId={projectId} />
      </TabPanel>

      {/* Call Sheets Tab */}
      <TabPanel value={tabValue} index={9} allowScroll={true}>
        <CallsheetMain projectId={projectId} projectName={project?.projectName || ''} />
      </TabPanel>

      {/* Resources Tab */}
      <TabPanel value={tabValue} index={10}>
        <Resources projectId={projectId} />
      </TabPanel>

      {/* Dailies Tab */}
      <TabPanel value={tabValue} index={11} allowScroll={true}>
        <Dailies projectId={projectId} />
      </TabPanel>

      {/* Budget Tab */}
      <TabPanel value={tabValue} index={12}>
        <Budget 
          projectId={projectId} 
          projectName={project?.title || project?.projectName || ''} 
          onError={(message) => setNotification({ open: true, message, severity: 'error' })}
          onBudgetChange={loadBudgetSummary}
        />
      </TabPanel>

      {/* Payroll Tab */}
      <TabPanel value={tabValue} index={13}>
        <Payroll />
      </TabPanel>

      {/* Dialogs */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
        severity={notification.severity}
      />

      {/* Global Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </PermissionsProvider>
  );
};

export default ProjectDashboard;
